import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import RBACApiService from '../../services/rbacApiService';

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadRoles();
        loadPermissions();
    }, []);

    const loadRoles = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await RBACApiService.getRoles();
            // Handle API response structure
            if (response && response.success && response.data) {
                setRoles(response.data);
            } else if (Array.isArray(response)) {
                setRoles(response);
            } else {
                setRoles([]);
                console.warn('Unexpected roles response format:', response);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            setError('Failed to load roles');
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPermissions = async () => {
        try {
            const response = await RBACApiService.getPermissions();
            // Handle API response structure
            if (response && response.success && response.data) {
                setPermissions(response.data);
            } else if (Array.isArray(response)) {
                setPermissions(response);
            } else {
                setPermissions([]);
                console.warn('Unexpected permissions response format:', response);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            setPermissions([]);
        }
    };

    const handleCreateRole = async (roleData) => {
        try {
            await RBACApiService.createRole(roleData);
            setOpenDialog(false);
            loadRoles();
        } catch (error) {
            console.error('Error creating role:', error);
            setError('Failed to create role');
        }
    };

    const handleUpdateRole = async (roleId, updates) => {
        try {
            await RBACApiService.updateRole(roleId, updates);
            setSelectedRole(null);
            loadRoles();
        } catch (error) {
            console.error('Error updating role:', error);
            setError('Failed to update role');
        }
    };

    const handleDeleteRole = async (roleId) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            try {
                await RBACApiService.deleteRole(roleId);
                loadRoles();
            } catch (error) {
                console.error('Error deleting role:', error);
                setError('Failed to delete role');
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Role Management
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Button variant="contained" onClick={() => setOpenDialog(true)} sx={{ mb: 3 }}>
                Create New Role
            </Button>

            <Card>
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Permissions</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {roles.map((role) => (
                                <TableRow key={role.id}>
                                    <TableCell>{role.name}</TableCell>
                                    <TableCell>{role.description}</TableCell>
                                    <TableCell>
                                        <Chip label={role.level} color="primary" size="small" />
                                    </TableCell>
                                    <TableCell>
                                        {role.permissions?.slice(0, 3).map((perm, index) => (
                                            <Chip key={index} label={perm} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                        {role.permissions?.length > 3 && (
                                            <Chip label={`+${role.permissions.length - 3} more`} size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => setSelectedRole(role)} sx={{ mr: 1 }}>
                                            Edit
                                        </Button>
                                        <Button size="small" color="error" onClick={() => handleDeleteRole(role.id)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Role Editor Dialog */}
            {selectedRole && (
                <RoleEditor
                    role={selectedRole}
                    permissions={permissions}
                    onSave={handleUpdateRole}
                    onCancel={() => setSelectedRole(null)}
                />
            )}

            {/* Create Role Dialog */}
            {openDialog && <RoleEditor permissions={permissions} onSave={handleCreateRole} onCancel={() => setOpenDialog(false)} />}
        </Box>
    );
};

// Role Editor Component
const RoleEditor = ({ role, permissions, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: role?.name || '',
        description: role?.description || '',
        level: role?.level || 'project',
        permissions: role?.permissions || []
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (role) {
            onSave(role.id, formData);
        } else {
            onSave(formData);
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle>{role ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Role Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Level</InputLabel>
                        <Select value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value })}>
                            <MenuItem value="platform">Platform</MenuItem>
                            <MenuItem value="organization">Organization</MenuItem>
                            <MenuItem value="project">Project</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Permissions
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', p: 1 }}>
                        {permissions.map((permission) => (
                            <Box key={permission.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <input
                                    type="checkbox"
                                    checked={formData.permissions.includes(`${permission.resource}:${permission.action}`)}
                                    onChange={(e) => {
                                        const permString = `${permission.resource}:${permission.action}`;
                                        if (e.target.checked) {
                                            setFormData({
                                                ...formData,
                                                permissions: [...formData.permissions, permString]
                                            });
                                        } else {
                                            setFormData({
                                                ...formData,
                                                permissions: formData.permissions.filter((p) => p !== permString)
                                            });
                                        }
                                    }}
                                />
                                <Typography sx={{ ml: 1 }}>
                                    {permission.resource}:{permission.action} - {permission.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {role ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleManagement;
